import s from './NewsContent.module.sass'

import { FC, useEffect, useRef } from 'react'
import { useAppSelector } from '@app/model/store/client'
import { NewsContentSkeleton } from '@pages/NewsItem/ui/NewsContent/index'
import NewsSocial from './NewsSocial'

import { QuickBookingArticle } from '@widgets/Restaurant/QuickBooking/variants/QuickBookingArticle'
import { isBookingAvailable } from '@shared/lib/booking/domain/providerRegistry'

import { EGG } from '@shared/api/analytics'
import { Text } from '@shared/ui/Typography/Text'
import { Title } from '@shared/ui/Typography/Title'
import { useClientResponsive } from '@shared/lib/hooks/useClientResponsive'
import { TextTag } from '@shared/ui/Typography/Text/types'

import IconQuote from '@icons/icon-quote.svg'

const getPropsText = (tag = 'p') => {
  switch (tag) {
    case 'p':
      return {
        sizes: 'ML M',
        paragraph: true,
      }
    case 'i':
      return {
        sizes: 'M',
      }
    case 'b':
      return {
        sizes: 'ML M',
        paragraph: true,
      }
    default:
      return {
        sizes: 'ML M',
        paragraph: true,
      }
  }
}

interface Props {
  className?: string
  listenRootSelector?: string
}

const NewsContent: FC<Props> = ({ className = '', listenRootSelector }) => {
  const { news, restaurants } = useAppSelector((store) => store.pages.news_item)
  const { isMobile } = useClientResponsive()
  const refContentMarkup = useRef<HTMLDivElement | null>(null)

  // Цель этого эффекта - покрыть аналитикой ссылки в статьях. Клики и просмотр.
  useEffect(() => {
    const getRestaurantSlugFromLink = (element: Element): string | null => {
      const anchor = element instanceof HTMLAnchorElement ? element : element.closest('a')
      return anchor?.href.match(/restaurant\/(.*?)\//)?.[1] ?? null
    }

    const handleSendLinkAnalytic = (element: Element, typeEvent: 'view' | 'click') => {
      const restSlug = getRestaurantSlugFromLink(element)
      const findRest = restSlug ? restaurants?.find((rest) => rest.url.slug === restSlug) : null

      if (!findRest) return

      const analyticMethods = {
        view: EGG.entity.news.rest_name_link_view,
        click: EGG.entity.news.rest_name_link_click,
      }

      const sectionName = element.closest('[data-analytic-section-name]') as HTMLElement | null
      analyticMethods[typeEvent](findRest, { section_name: sectionName?.dataset.analyticSectionName || 'title' })
    }

    const root = listenRootSelector ? document.querySelector(listenRootSelector) : refContentMarkup.current
    const targets = [...(root?.querySelectorAll('a[href]') ?? [])].filter(
      (el): el is HTMLAnchorElement =>
        el instanceof HTMLAnchorElement && el.href.match(/restaurant\/(.*?)\//g)?.length === 1,
    )

    const handleClickLink = (e: Event) => {
      handleSendLinkAnalytic(e.currentTarget as HTMLAnchorElement, 'click')
    }

    const callbackObserver = (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        observer.unobserve(entry.target)
        handleSendLinkAnalytic(entry.target as HTMLAnchorElement, 'view')
      })
    }

    const observer = new IntersectionObserver(callbackObserver, {
      root: null,
      rootMargin: '5px',
      threshold: 0.9,
    })

    targets.forEach((target) => {
      target.addEventListener('click', handleClickLink)
      observer.observe(target)
    })

    return () => {
      observer.disconnect()
      targets.forEach((target) => {
        target.removeEventListener('click', handleClickLink)
      })
    }
  }, [listenRootSelector, news, restaurants])

  if (listenRootSelector) return null

  if (!news) return <NewsContentSkeleton className={className} />

  return (
    <div className={`${s.content} ${className}`} ref={refContentMarkup}>
      <Text className={s['content-announce']} sizes={'XL ML'} paragraph>
        {news.description.announce}
      </Text>

      {news.markup &&
        news.markup.map((item, index) => {
          const isSocial =
            index == news.markup.length - 1 && item?.data?.text && item.data.text.toLowerCase().includes('скидки')

          const isQuote = item.type === 'quote'
          const isNextTagI = news.markup[index + 1]?.type === 'text' && news.markup[index + 1]?.data?.tag === 'i'
          const hasImage = item.type === 'image' && item?.data?.file?.url
          const hasHeading = item.type === 'heading' && item?.data?.text

          const restQuickBooking =
            item.type === 'quick-booking' && restaurants?.find((rest) => rest.id === item.data.id)

          const hasQuickBooking =
            !!restQuickBooking && restQuickBooking.status.open && isBookingAvailable(restQuickBooking?.booking)

          if (hasImage) {
            return (
              <div
                key={index}
                className={s['content-img']}
                data-has-alt={isNextTagI}
                style={{ width: '100%', aspectRatio: item?.data?.file?.ratio, position: 'relative' }}>
                <img
                  style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute' }}
                  alt={`Фотография ${index}`}
                  src={item?.data?.file?.url || ''}
                />
              </div>
            )
          } else if (hasHeading) {
            return (
              <Title
                key={index}
                data-analytic-section-name={'title'}
                data-has-alt={isNextTagI}
                className={s['content-header']}
                tag={'h2'}
                sizes={'h3 h5'}>
                <span dangerouslySetInnerHTML={{ __html: item?.data?.text || '' }} />
              </Title>
            )
          } else if (hasQuickBooking) {
            return <QuickBookingArticle key={index} restData={restQuickBooking} className={s['quick-booking']} />
          } else if (isSocial) {
            return <NewsSocial key={index} />
          } else if (isQuote) {
            return (
              <blockquote key={index} className={s['content-blockquote']}>
                <IconQuote />
                <Text sizes={'XL ML'} medium paragraph={!isMobile}>
                  {item.data.text}
                </Text>
                <Text tag={'cite'} sizes={'M S'}>
                  {item.data.cite}
                </Text>
              </blockquote>
            )
          } else {
            return (
              <Text
                key={index}
                className={s['content-text']}
                data-analytic-section-name={'paragraph'}
                tag={(item.data.tag as TextTag) || 'p'}
                {...getPropsText(item.data.tag)}>
                <span dangerouslySetInnerHTML={{ __html: item.data.text || '' }} />
              </Text>
            )
          }
        })}
    </div>
  )
}

export default NewsContent
